<template>
  <div class="space-y-3">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold">Layers</h3>
    </div>

    <!-- Layer List with unified drag-drop -->
    <div class="space-y-1">
      <LayerRow
        v-for="(layer, index) in displayLayers"
        :key="layer.id"
        :layer="layer"
        :is-selected="selectedLayerId === layer.id"
        :index="index"
        @select="$emit('selectLayer', $event)"
        @toggle-visibility="handleToggleVisibility"
        @toggle-lock="handleToggleLock"
        @delete="$emit('deleteLayer', $event)"
        @reset="handleResetLayer"
        @reorder="handleReorder"
      />

      <!-- Empty State -->
      <div v-if="layers.length === 0" class="text-center py-8 text-muted-foreground">
        <Layers class="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p class="text-xs">No layers yet</p>
        <p class="text-xs">Drag images from the library</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Layers } from 'lucide-vue-next'
import LayerRow from './LayerRow.vue'
import type { DisplayLayer } from '@/stores/sceneDisplayConfigs'

interface Props {
  layers: DisplayLayer[]
  selectedLayerId: string | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  selectLayer: [layerId: string]
  reorderLayers: [layers: DisplayLayer[]]
  deleteLayer: [layerId: string]
  updateLayer: [layer: DisplayLayer]
  resetLayer: [layerId: string]
}>()

// Display layers in reverse order (highest z-index at top)
const displayLayers = computed(() => {
  return [...props.layers].reverse()
})

// Handle visibility toggle
const handleToggleVisibility = (layer: DisplayLayer) => {
  emit('updateLayer', { ...layer, visible: !layer.visible })
}

// Handle lock toggle
const handleToggleLock = (layer: DisplayLayer) => {
  emit('updateLayer', { ...layer, locked: !layer.locked })
}

// Handle reset for inherited layers
const handleResetLayer = (layerId: string) => {
  emit('resetLayer', layerId)
}

// Handle reorder from drag-drop
const handleReorder = (fromIndex: number, toIndex: number) => {
  // Convert display indices to layer array order
  const currentLayers = [...displayLayers.value]
  
  // Remove item from source position
  const [movedItem] = currentLayers.splice(fromIndex, 1)
  
  // Insert at target position
  currentLayers.splice(toIndex, 0, movedItem)
  
  // Reverse back to z-index order and update z-indexes
  const reversedLayers = [...currentLayers].reverse()
  const updatedLayers = reversedLayers.map((layer, index) => ({
    ...layer,
    zIndex: index,
    z_index: index
  }))
  
  emit('reorderLayers', updatedLayers)
}
</script>